import * as THREE from 'three';
import type { MapDef, Zone } from '@somnhold/sim';
import { WORLD_PER_HEX } from './coords.ts';
import type { TerrainBuild } from './terrain.ts';
import type { CryptBuild } from './crypt.ts';

/*
 * Ascuas (incursion level II): the same clearing, burnt to basalt and split by seams of fire.
 *
 * - The skin: the ground is repainted in charcoal and ash, glowing veins run through it (in the ground's
 *   own shader, no extra draw), the dusk turns red and embers drift up over the board (one Points draw).
 * - Lava cracks (`zone.kind === 'lava'`): fixed pools of molten rock with a bright rim, draped over the
 *   ground and merged into one mesh (rebuilt only when the set of cracks changes).
 * - Eruptions (`'eruption'`): a red telegraph that fills toward its rim and a ring that closes on it as the
 *   countdown runs out; all of them share one dynamic mesh.
 * Bursts, sparks and flying rock are drawn by the BattleView with the shared VFX helpers.
 */

const K = WORLD_PER_HEX / 1000;
const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

/* ---------- the vein texture ---------- */

let veinTex: THREE.DataTexture | null = null;

/**
 * A tileable 256² map: R the distance to the nearest Voronoi edge (0 on a crack, the plates in between),
 * G a random value per plate (each seam breathes in its own phase), B smooth value noise (where the
 * cracks are alive). Generated once, never downloaded.
 */
export const veinTexture = (): THREE.DataTexture => {
  if (veinTex) return veinTex;
  const N = 256;
  const CELLS = 8;
  let k = 0x9e3779b9;
  const rnd = (): number => {
    k = (Math.imul(k ^ (k >>> 15), 2246822519) + 0x6d2b79f5) >>> 0;
    return k / 4294967296;
  };
  const pts: [number, number, number][] = [];
  for (let j = 0; j < CELLS; j++) for (let i = 0; i < CELLS; i++) pts.push([(i + 0.15 + rnd() * 0.7) / CELLS, (j + 0.15 + rnd() * 0.7) / CELLS, rnd()]);
  const P = 16;
  const grid = Array.from({ length: P * P }, () => rnd());
  const vnoise = (x: number, y: number): number => {
    let s = 0;
    let amp = 0.5;
    let norm = 0;
    for (let o = 0, f = 4; o < 3; o++, f *= 2) {
      const fx = x * f, fy = y * f;
      const ix = Math.floor(fx), iy = Math.floor(fy);
      const tx = fx - ix, ty = fy - iy;
      const ux = tx * tx * (3 - 2 * tx), uy = ty * ty * (3 - 2 * ty);
      const at = (a: number, b: number): number => grid[(((b % f) + f) % f % P) * P + ((((a % f) + f) % f) % P)] as number;
      const a = at(ix, iy), b = at(ix + 1, iy), c = at(ix, iy + 1), d = at(ix + 1, iy + 1);
      s += (a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy) * amp;
      norm += amp;
      amp *= 0.5;
    }
    return s / norm;
  };
  const data = new Uint8Array(N * N * 4);
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      const u = (x + 0.5) / N, v = (y + 0.5) / N;
      // Wobble the lookup a little so the seams wander instead of running straight.
      const wu = u + (vnoise(u + 0.31, v) - 0.5) * 0.035;
      const wv = v + (vnoise(u, v + 0.57) - 0.5) * 0.035;
      let d1 = 9, d2 = 9, id = 0;
      for (const [px, py, h] of pts) {
        for (let oy = -1; oy <= 1; oy++) {
          for (let ox = -1; ox <= 1; ox++) {
            const dx = px + ox - wu, dy = py + oy - wv;
            const d = Math.sqrt(dx * dx + dy * dy);
            if (d < d1) {
              d2 = d1;
              d1 = d;
              id = h;
            } else if (d < d2) d2 = d;
          }
        }
      }
      const o = (y * N + x) * 4;
      data[o] = Math.round(clamp01(((d2 - d1) * CELLS) / 0.9) * 255);
      data[o + 1] = Math.round(id * 255);
      data[o + 2] = Math.round(clamp01((vnoise(u, v) - 0.5) * 1.8 + 0.5) * 255);
      data[o + 3] = 255;
    }
  }
  const t = new THREE.DataTexture(data, N, N, THREE.RGBAFormat);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.generateMipmaps = true;
  t.needsUpdate = true;
  veinTex = t;
  return t;
};

/* ---------- the ground skin ---------- */

export const VEIN_PARS = /* glsl */ `
uniform sampler2D tVein;
uniform float uAscTime;
uniform float uAscClear;
varying vec3 vAscW;`;

/** Seams of fire through the ground: thin and dim on the clearing, wide rivers in the ravines outside. */
const VEIN_FRAG = /* glsl */ `
{
  vec2 p = vAscW.xz;
  vec4 a = texture2D(tVein, p * 0.05);
  vec4 b = texture2D(tVein, p * 0.13 + vec2(0.37, 0.61));
  float n = texture2D(tVein, p * 0.021 + vec2(0.5, 0.2)).b;
  float n2 = texture2D(tVein, p * 0.07 + vec2(0.13, 0.77)).b;
  float outside = smoothstep(uAscClear * 0.85, uAscClear * 1.5, length(p));
  float low = smoothstep(-0.2, -0.55, vAscW.y);
  float w = mix(0.022, 0.045, outside) + low * 0.11;
  float mask = smoothstep(0.5, 0.66, n * 0.65 + n2 * 0.35 + low * 0.35) * mix(0.4, 1.0, outside);
  float c1 = 1.0 - smoothstep(0.0, w, a.r);
  float c2 = (1.0 - smoothstep(0.0, w * 0.6, b.r)) * 0.35 * outside;
  float crack = max(c1, c2) * mask;
  float halo = (1.0 - smoothstep(0.0, w * 4.0, a.r)) * mask;
  float beat = 0.7 + 0.3 * sin(uAscTime * (0.8 + a.g * 0.9) + a.g * 6.2831);
  diffuseColor.rgb *= 1.0 - 0.3 * halo;
  diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.03, 0.006, 0.003), crack);
  vec3 hotC = mix(vec3(0.8, 0.05, 0.012), vec3(1.0, 0.42, 0.08), crack * crack);
  totalEmissiveRadiance += hotC * crack * beat * mix(0.7, 1.5, outside) + vec3(0.28, 0.035, 0.0) * halo * beat * 0.18 * outside;
}`;

/**
 * The crypt in embers: fire glows up through the grout between the flagstones (the grout sits a little
 * lower, so it is found by height), thin seams split the stones, and a faint red underglow climbs the
 * foot of the walls. Face orientation comes from the derivatives of the world position (flat shading).
 */
export const CRYPT_FRAG = /* glsl */ `
{
  vec2 p = vAscW.xz;
  vec3 wn = normalize(cross(dFdx(vAscW), dFdy(vAscW)));
  float up = smoothstep(0.75, 0.95, abs(wn.y));
  float floorY = 1.0 - smoothstep(0.08, 0.2, vAscW.y);
  vec4 a = texture2D(tVein, p * 0.09);
  float n = texture2D(tVein, p * 0.03 + vec2(0.5, 0.2)).b;
  float n2 = texture2D(tVein, p * 0.11 + vec2(0.13, 0.77)).b;
  float live = smoothstep(0.42, 0.62, n * 0.6 + n2 * 0.4);
  float beat = 0.7 + 0.3 * sin(uAscTime * (0.8 + a.g * 0.9) + a.g * 6.2831);
  float grout = (1.0 - smoothstep(-0.03, -0.02, vAscW.y)) * up * floorY;
  float crack = (1.0 - smoothstep(0.0, 0.03, a.r)) * live * up * floorY * (1.0 - grout);
  float halo = (1.0 - smoothstep(0.0, 0.12, a.r)) * live * up * floorY;
  diffuseColor.rgb *= 1.0 - 0.25 * halo;
  diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.03, 0.006, 0.003), crack);
  vec3 hotC = mix(vec3(0.8, 0.05, 0.012), vec3(1.0, 0.42, 0.08), crack * crack);
  totalEmissiveRadiance += hotC * crack * beat * 0.9;
  float groutLive = smoothstep(0.5, 0.72, n * 0.5 + n2 * 0.5);
  totalEmissiveRadiance += vec3(0.5, 0.045, 0.012) * grout * (0.08 + groutLive) * beat * 0.4;
  float wall = (1.0 - up) * (1.0 - smoothstep(-0.1, 0.3, vAscW.y)) * step(-0.35, vAscW.y);
  totalEmissiveRadiance += vec3(0.09, 0.008, 0.002) * wall * (0.6 + 0.4 * beat);
}`;

const skinMaterial = (src: THREE.MeshStandardMaterial, uniforms: Record<string, THREE.IUniform>, crypt = false): THREE.MeshStandardMaterial => {
  const m = src.clone();
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vAscW;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvAscW = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${VEIN_PARS}`)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>\n${crypt ? CRYPT_FRAG : VEIN_FRAG}`);
  };
  m.customProgramCacheKey = () => (crypt ? 'somnhold-ascuas-crypt' : 'somnhold-ascuas-ground');
  m.needsUpdate = true;
  return m;
};

const lum = (r: number, g: number, b: number): number => 0.2126 * r + 0.7152 * g + 0.0722 * b;

/** Repaints a vertex-coloured mesh in basalt: the old light and shade survive, the hue goes to ash. */
const basaltify = (geo: THREE.BufferGeometry, dark: THREE.Color, light: THREE.Color, lo: number, hi: number): void => {
  const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
  if (!col) return;
  const c = new THREE.Color();
  for (let i = 0; i < col.count; i++) {
    const t = clamp01((lum(col.getX(i), col.getY(i), col.getZ(i)) - lo) / (hi - lo));
    c.copy(dark).lerp(light, t);
    col.setXYZ(i, c.r, c.g, c.b);
  }
  col.needsUpdate = true;
};

export const CRYPT = {
  floorDark: new THREE.Color('#141112'),
  floorLight: new THREE.Color('#38302f'),
  grout: new THREE.Color('#0a0605'),
  wallDark: new THREE.Color('#090707'),
  wallLight: new THREE.Color('#2a2425'),
};

const BASALT = {
  groundDark: new THREE.Color('#0f0d0d'),
  groundLight: new THREE.Color('#3b3634'),
  tileDark: new THREE.Color('#252222'),
  tileLight: new THREE.Color('#5f5b5c'),
  roadDark: new THREE.Color('#1e1815'),
  roadLight: new THREE.Color('#4a3d35'),
  char: new THREE.Color('#171312'),
  ember: new THREE.Color('#ff6a2a'),
};

/* ---------- embers ---------- */

const EMBER_VERT = /* glsl */ `
attribute vec4 aSeed;
uniform float uTime;
uniform float uPx;
uniform float uSize;
varying float vAlpha;
varying float vHot;
void main() {
  float t = uTime * (0.6 + aSeed.w * 0.6) + aSeed.z * 37.0;
  float life = fract(t * 0.07);
  vec3 p = vec3(aSeed.x, life * 5.5, aSeed.y);
  p.x += sin(t * 0.7 + aSeed.z * 6.28) * 0.5 + life * 1.4;
  p.z += cos(t * 0.53 + aSeed.w * 6.28) * 0.5 + life * 0.5;
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  float flick = 0.55 + 0.45 * sin(uTime * 11.0 + aSeed.z * 61.0);
  vAlpha = smoothstep(0.0, 0.08, life) * (1.0 - smoothstep(0.55, 1.0, life)) * flick;
  vHot = aSeed.w;
  gl_PointSize = uSize * (0.45 + aSeed.z) * uPx / max(0.5, -mv.z);
  gl_Position = projectionMatrix * mv;
}`;

const EMBER_FRAG = /* glsl */ `
varying float vAlpha;
varying float vHot;
void main() {
  vec2 c = gl_PointCoord - 0.5;
  float d = length(c) * 2.0;
  float a = (1.0 - smoothstep(0.2, 1.0, d)) * vAlpha;
  vec3 col = mix(vec3(1.0, 0.28, 0.06), vec3(1.0, 0.72, 0.32), vHot * (1.0 - d));
  gl_FragColor = vec4(col, a);
}`;

/* ---------- lava and eruption decals ---------- */

const DECAL_VERT = /* glsl */ `
attribute vec2 aLocal;
attribute vec2 aSeed;
varying vec2 vL;
varying vec2 vS;
void main() {
  vL = aLocal;
  vS = aSeed;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

/** Molten pool: crust plates drifting on fire, a white-hot heart, a red rim right on the danger line, scorch beyond. */
const LAVA_FRAG = /* glsl */ `
uniform sampler2D tVein;
uniform float uTime;
varying vec2 vL;
varying vec2 vS;
void main() {
  float d = length(vL);
  float ang = atan(vL.y, vL.x);
  float en = texture2D(tVein, vec2(ang * 0.4775 + vS.x, vS.x * 0.37 + 0.11)).b;
  float edge = 0.9 + 0.1 * en;
  vec2 flow = vec2(uTime * 0.011, -uTime * 0.008);
  vec4 v = texture2D(tVein, vL * 0.2 + vS.xx * vec2(1.0, 1.7) + flow);
  vec4 v2 = texture2D(tVein, vL * 0.45 + vS.xx * 2.3 - flow * 1.6);
  float r = d / edge;
  float core = 1.0 - smoothstep(0.0, 0.8, r);
  float seam = max(1.0 - smoothstep(0.0, 0.11 + 0.12 * core, v.r), (1.0 - smoothstep(0.0, 0.07, v2.r)) * 0.6);
  float pulse = 0.82 + 0.18 * sin(uTime * 2.1 + vS.x * 6.0 + v.g * 6.2831);
  vec3 crust = mix(vec3(0.045, 0.012, 0.008), vec3(0.32, 0.045, 0.01), core * 0.8 + v.b * 0.25);
  vec3 molten = mix(vec3(1.0, 0.22, 0.04), vec3(1.0, 0.7, 0.28), core * core);
  float m = max(seam * (0.55 + 0.45 * core), core * core * 0.9);
  vec3 col = mix(crust, molten * 1.25 * pulse, m);
  float inside = 1.0 - smoothstep(edge - 0.02, edge, d);
  float rim = exp(-pow((d - edge) / 0.03, 2.0));
  vec3 red = vec3(1.0, 0.16, 0.08);
  col = mix(vec3(0.025, 0.008, 0.006), col, inside) + red * rim * (0.75 + 0.3 * pulse);
  float scorch = (1.0 - smoothstep(edge, 1.22, d)) * 0.8;
  gl_FragColor = vec4(col, clamp(max(max(inside, rim), scorch), 0.0, 1.0));
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

/**
 * Eruption telegraph (additive): a hard red line on the radius, a ring closing in on it from outside,
 * and a glowing fill spreading from the centre to the rim as the countdown (vS.y: 0 → 1) runs out. Red only
 * where it hits: the closing ring and the ticks just outside the line are pale, a clock rather than fire.
 */
const ERUPT_FRAG = /* glsl */ `
uniform sampler2D tVein;
uniform float uTime;
varying vec2 vL;
varying vec2 vS;
void main() {
  float d = length(vL);
  float p = vS.y;
  float blink = 0.6 + 0.4 * sin(uTime * (5.0 + 16.0 * p) + vS.x * 9.0);
  float line = exp(-pow((d - 1.0) / 0.035, 2.0)) * (0.9 + 0.6 * blink);
  float closeAt = 1.0 + (1.0 - p) * 0.55;
  float closing = exp(-pow((d - closeAt) / 0.05, 2.0)) * (0.35 + 0.65 * p) * (1.0 - step(0.999, p));
  float fill = (1.0 - smoothstep(p * 1.02 - 0.06, p * 1.02, d)) * (1.0 - smoothstep(0.97, 1.0, d));
  float base = (1.0 - smoothstep(0.96, 1.0, d)) * (0.12 + 0.25 * p);
  vec4 v = texture2D(tVein, vL * 0.3 + vS.xx);
  float cracks = (1.0 - smoothstep(0.0, 0.05 + 0.1 * p, v.r)) * fill;
  float tick = step(0.5, fract(atan(vL.y, vL.x) * 1.9099 - uTime * 0.6)) * exp(-pow((d - 1.07) / 0.025, 2.0)) * 0.7;
  vec3 red = vec3(1.0, 0.12, 0.05);
  vec3 pale = vec3(0.55, 0.58, 0.62);
  vec3 col = red * (line * 0.85 + base * blink) + pale * (closing * 0.8 + tick) + vec3(0.55, 0.05, 0.01) * fill * (0.2 + 0.3 * p) + vec3(1.0, 0.4, 0.1) * cracks * (0.4 + 0.9 * p);
  gl_FragColor = vec4(col, 1.0);
  #include <colorspace_fragment>
}`;


/**
 * A fire vent (trap): an iron grate set in a ring of scorched stone over a fissure. Idle, the fire below
 * smoulders dull red through the bars; as its eruption's countdown runs (vS.y: 0 → 1) it brightens to
 * white-hot and the bars glow at their edges.
 */
const VENT_FRAG = /* glsl */ `
uniform sampler2D tVein;
uniform float uTime;
varying vec2 vL;
varying vec2 vS;
void main() {
  float d = length(vL);
  float h = vS.y;
  float ca = cos(vS.x), sa = sin(vS.x);
  vec2 q = mat2(ca, -sa, sa, ca) * vL;
  // Bars: a square lattice, heavier one way.
  vec2 g = abs(fract(q * vec2(3.2, 1.6)) - 0.5);
  float bar = max(1.0 - smoothstep(0.13, 0.19, g.x), (1.0 - smoothstep(0.08, 0.13, g.y)));
  float frame = smoothstep(0.66, 0.7, d) * (1.0 - smoothstep(0.84, 0.88, d));
  float hole = 1.0 - smoothstep(0.66, 0.7, d);
  vec4 v = texture2D(tVein, vL * 0.35 + vS.xx * 0.13 + vec2(uTime * 0.01, 0.0));
  float fis = 1.0 - smoothstep(0.0, 0.18 + 0.2 * h, v.r);
  float beat = 0.75 + 0.25 * sin(uTime * (2.0 + 9.0 * h) + vS.x * 3.0);
  vec3 pit = mix(vec3(0.02, 0.006, 0.004), vec3(0.45, 0.04, 0.01), fis * (0.5 + 0.5 * beat));
  vec3 fire = mix(vec3(0.9, 0.12, 0.03), vec3(1.0, 0.75, 0.35), h * h);
  pit = mix(pit, fire * (0.7 + 0.5 * beat), h * (0.35 + 0.65 * fis));
  vec3 iron = mix(vec3(0.018, 0.016, 0.018), vec3(0.04, 0.035, 0.035), v.b);
  iron += vec3(0.6, 0.08, 0.02) * h * h * (1.0 - smoothstep(0.0, 0.5, max(g.x, g.y))) * 0.6;
  vec3 col = mix(pit, iron, bar * hole);
  col = mix(col, iron * 1.25, frame);
  // Scorched stone round the frame, its rim glowing faintly.
  float scorch = (1.0 - smoothstep(0.88, 1.15, d)) * (1.0 - hole) * (1.0 - frame);
  float rim = exp(-pow((d - 0.69) / 0.025, 2.0)) * (0.25 + 0.75 * h);
  col = mix(col, vec3(0.03, 0.012, 0.01), scorch * 0.8) + vec3(1.0, 0.2, 0.06) * rim * 0.8;
  float a = max(max(hole, frame), scorch * 0.85);
  gl_FragColor = vec4(col, clamp(a, 0.0, 1.0));
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

interface Draped {
  pos: Float32Array;
  local: Float32Array;
  index: number[];
}

const RINGS = 14;
const SEGS = 56;
/** The ground under a mark is sampled around each vertex and the highest taken: tiles step in height, and a
 * mark laid only at its vertices dips under a taller tile between them (that is what flickered). */
const SPREAD = 0.22;

/** A disc (out to `reach` × radius) laid over the ground, in world space; local coordinates are in radii. */
const drape = (cx: number, cz: number, r: number, reach: number, ground0: (x: number, z: number) => number, lift: number): Draped => {
  const ground = (x: number, z: number): number =>
    Math.max(ground0(x, z), ground0(x + SPREAD, z), ground0(x - SPREAD, z), ground0(x, z + SPREAD), ground0(x, z - SPREAD));
  const pos: number[] = [cx, ground(cx, cz) + lift, cz];
  const local: number[] = [0, 0];
  const index: number[] = [];
  for (let i = 1; i <= RINGS; i++) {
    const k = (i / RINGS) * reach;
    for (let s = 0; s < SEGS; s++) {
      const a = (s / SEGS) * Math.PI * 2;
      const lx = Math.cos(a) * k, lz = Math.sin(a) * k;
      const x = cx + lx * r, z = cz + lz * r;
      pos.push(x, ground(x, z) + lift, z);
      local.push(lx, lz);
    }
  }
  for (let s = 0; s < SEGS; s++) index.push(0, 1 + ((s + 1) % SEGS), 1 + s);
  for (let i = 1; i < RINGS; i++) {
    const a0 = 1 + (i - 1) * SEGS, b0 = 1 + i * SEGS;
    for (let s = 0; s < SEGS; s++) {
      const s1 = (s + 1) % SEGS;
      index.push(a0 + s, a0 + s1, b0 + s, a0 + s1, b0 + s1, b0 + s);
    }
  }
  return { pos: new Float32Array(pos), local: new Float32Array(local), index };
};
const VERTS = 1 + RINGS * SEGS;

const decalMaterial = (frag: string, uniforms: Record<string, THREE.IUniform>, additive: boolean): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms,
    vertexShader: DECAL_VERT,
    fragmentShader: frag,
    transparent: true,
    depthWrite: false,
    blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
    // Flat on the ground: pulled toward the camera in the depth test like the other floor marks.
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -8,
  });

export interface AscuasSky {
  hemi: THREE.HemisphereLight;
  key: THREE.DirectionalLight;
  rim: THREE.DirectionalLight;
}

const MAX_ERUPT = 12;

export class Ascuas {
  readonly group = new THREE.Group();
  private uniforms = { tVein: { value: veinTexture() }, uTime: { value: 0 } };
  private groundUniforms = { tVein: this.uniforms.tVein, uAscTime: this.uniforms.uTime, uAscClear: { value: 5 } };
  private embers: THREE.Points;
  private emberU = { uTime: this.uniforms.uTime, uPx: { value: 400 }, uSize: { value: 0.16 } };
  private lava: THREE.Mesh;
  private lavaKey = '';
  private erupt: THREE.Mesh;
  private eruptKey = '';
  private eruptProg: THREE.BufferAttribute;
  /** Fire vents (crypt traps): one merged mesh, the heat of each written per vertex every tick. */
  private vents: THREE.Mesh;
  private ventKey = '';
  private ventList: Zone[] = [];
  private drapes = new Map<number, Draped>();
  /** The countdown each eruption started from (so the fill runs 0 → 1 whatever the sim's lead time). */
  private eruptMax = new Map<number, number>();

  constructor(
    private map: MapDef,
    private ground: (x: number, z: number) => number,
    low: boolean,
    /** Where embers may rise (world x, z); by default all over the board. The crypt passes its floor. */
    spots?: readonly (readonly [number, number])[],
  ) {
    this.group.name = 'ascuas';
    this.groundUniforms.uAscClear.value = (map.bastion + 0.5) * WORLD_PER_HEX;
    // Embers over the whole board, thicker out in the burnt land than on the clearing.
    const n = spots ? Math.min(low ? 120 : 260, spots.length * 3) : low ? 220 : 520;
    const seeds = new Float32Array(n * 4);
    let k = 0x2545f491;
    const rnd = (): number => {
      k = (Math.imul(k ^ (k >>> 15), 2246822519) + 0x9e3779b9) >>> 0;
      return k / 4294967296;
    };
    const R = (map.radius - 1) * WORLD_PER_HEX;
    for (let i = 0; i < n; i++) {
      if (spots && spots.length > 0) {
        // The rise drifts +x as it climbs: start a little upwind of the floor cell.
        const sp = spots[Math.floor(rnd() * spots.length)] as readonly [number, number];
        seeds.set([sp[0] + (rnd() - 0.5) * 0.8 - 0.7, sp[1] + (rnd() - 0.5) * 0.8 - 0.25, rnd(), rnd()], i * 4);
        continue;
      }
      const a = rnd() * Math.PI * 2;
      const d = Math.sqrt(0.04 + rnd() * 0.96) * R;
      seeds.set([Math.cos(a) * d - 1, Math.sin(a) * d, rnd(), rnd()], i * 4);
    }
    const eg = new THREE.BufferGeometry();
    eg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    eg.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 4));
    this.embers = new THREE.Points(
      eg,
      new THREE.ShaderMaterial({ uniforms: this.emberU, vertexShader: EMBER_VERT, fragmentShader: EMBER_FRAG, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }),
    );
    this.embers.frustumCulled = false;
    this.embers.renderOrder = 8;
    const px = new THREE.Vector2();
    this.embers.onBeforeRender = (renderer) => {
      this.emberU.uPx.value = renderer.getDrawingBufferSize(px).y * 0.5;
    };
    // Careful compiling these ahead while they have no geometry yet: three keys a program on whether the geometry
    // has a position attribute, so an empty one compiles a variant never drawn (docs/RENDIMIENTO.md, «Trampas»).
    this.lava = new THREE.Mesh(new THREE.BufferGeometry(), decalMaterial(LAVA_FRAG, this.uniforms, false));
    this.lava.frustumCulled = false;
    this.lava.renderOrder = 1;
    this.lava.visible = false;
    const eg2 = new THREE.BufferGeometry();
    eg2.setAttribute('position', new THREE.BufferAttribute(new Float32Array(MAX_ERUPT * VERTS * 3), 3).setUsage(THREE.DynamicDrawUsage));
    eg2.setAttribute('aLocal', new THREE.BufferAttribute(new Float32Array(MAX_ERUPT * VERTS * 2), 2).setUsage(THREE.DynamicDrawUsage));
    this.eruptProg = new THREE.BufferAttribute(new Float32Array(MAX_ERUPT * VERTS * 2), 2).setUsage(THREE.DynamicDrawUsage);
    eg2.setAttribute('aSeed', this.eruptProg);
    this.erupt = new THREE.Mesh(eg2, decalMaterial(ERUPT_FRAG, this.uniforms, true));
    this.erupt.frustumCulled = false;
    this.erupt.renderOrder = 2;
    this.erupt.visible = false;
    this.vents = new THREE.Mesh(new THREE.BufferGeometry(), decalMaterial(VENT_FRAG, this.uniforms, false));
    this.vents.frustumCulled = false;
    this.vents.renderOrder = 1;
    this.vents.visible = false;
    this.group.add(this.embers, this.lava, this.vents, this.erupt);
  }

  /** Burns the board: basalt ground, veins of fire, charred woods, a red dusk. Call once. */
  apply(scene: THREE.Scene, terrain: TerrainBuild, sky: AscuasSky | null, extra: THREE.Object3D[]): void {
    const tiles = terrain.mesh;
    basaltify(tiles.geometry, BASALT.tileDark, BASALT.tileLight, 0.03, 0.2);
    tiles.material = skinMaterial(tiles.material as THREE.MeshStandardMaterial, this.groundUniforms);
    const c = new THREE.Color();
    const char = (o: THREE.Object3D): void => {
      const im = o as THREE.InstancedMesh;
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      if (o.name === 'outskirts-ground') {
        basaltify(mesh.geometry, BASALT.groundDark, BASALT.groundLight, 0.008, 0.11);
        mesh.material = skinMaterial(mesh.material as THREE.MeshStandardMaterial, this.groundUniforms);
        return;
      }
      if (o.name === 'roads') {
        basaltify(mesh.geometry, BASALT.roadDark, BASALT.roadLight, 0.02, 0.14);
        return;
      }
      const mat = mesh.material as THREE.MeshStandardMaterial;
      if (!mat?.isMeshStandardMaterial) return;
      // Lit things (the fog's crystals) turn to cinders of fire.
      if (mat.emissive && mat.emissive.getHex() !== 0) {
        mesh.material = new THREE.MeshStandardMaterial({ color: '#ff8a3d', emissive: '#ff3d0a', emissiveIntensity: 1.6, roughness: 0.4, flatShading: true });
        return;
      }
      if (im.isInstancedMesh && im.instanceColor) {
        // Woods burnt to black stumps and cones of char, boulders of basalt, grass to ash.
        const ash = o.name === 'tufts';
        for (let i = 0; i < im.count; i++) {
          im.getColorAt(i, c);
          const l = lum(c.r, c.g, c.b);
          c.copy(BASALT.char).lerp(ash ? BASALT.roadLight : BASALT.groundLight, clamp01(l / 0.09) * (ash ? 0.6 : 0.45));
          im.setColorAt(i, c);
        }
        im.instanceColor.needsUpdate = true;
        return;
      }
      if (mat.vertexColors || mat.map) return;
      const m2 = mat.clone();
      m2.color.lerp(BASALT.char, 0.75);
      mesh.material = m2;
    };
    terrain.decor.traverse(char);
    for (const o of extra) o.traverse(char);
    // A red dusk: smoky sky, ash fog, a low sun the colour of embers and a fiery rim from below.
    scene.background = new THREE.Color('#170c0b');
    if (scene.fog instanceof THREE.Fog) scene.fog.color.set('#2b1411');
    terrain.fog?.tint({ low: '#4e2a23', mid: '#361c18', far: '#170c0b', alpha: 1 });
    if (sky) {
      sky.hemi.color.set('#8e8c94');
      sky.hemi.groundColor.set('#33130a');
      sky.hemi.intensity *= 0.8;
      sky.key.color.set('#ffe2d0');
      sky.key.intensity *= 0.8;
      sky.rim.color.set('#b86a58');
      sky.rim.intensity *= 0.8;
    }
  }

  /**
   * The crypt in embers: charred basalt flagstones with fire in the grout, walls blackened with a red
   * glow at their foot, torches burning hot and red, the Warden's runes gone to fire, a red-black murk.
   * Call once.
   */
  applyCrypt(scene: THREE.Scene, c: CryptBuild, lights: { hemi: THREE.HemisphereLight | null; moon: THREE.DirectionalLight | null; rooms: THREE.PointLight[] }): void {
    const mesh = c.mesh;
    const geo = mesh.geometry;
    const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    if (col) {
      const k = new THREE.Color();
      for (let i = 0; i + 2 < col.count; i += 3) {
        // Non-indexed triangles: a wall's triangles reach above the floor.
        const top = Math.max(pos.getY(i), pos.getY(i + 1), pos.getY(i + 2));
        const wall = top > 0.3;
        for (let j = i; j < i + 3; j++) {
          const l = lum(col.getX(j), col.getY(j), col.getZ(j));
          if (wall) k.copy(CRYPT.wallDark).lerp(CRYPT.wallLight, clamp01((l - 0.005) / 0.05));
          else if (pos.getY(j) < -0.03 && top < -0.02) k.copy(CRYPT.grout);
          else k.copy(CRYPT.floorDark).lerp(CRYPT.floorLight, clamp01((l - 0.01) / 0.05));
          col.setXYZ(j, k.r, k.g, k.b);
        }
      }
      col.needsUpdate = true;
    }
    mesh.material = skinMaterial(mesh.material as THREE.MeshStandardMaterial, this.groundUniforms, true);
    // Torches burn hotter and redder; the Warden's violet goes to fire.
    const flameM = new THREE.MeshStandardMaterial({ color: '#ffd2a0', emissive: '#ff3d12', emissiveIntensity: 2.2, transparent: true, opacity: 0.95, depthWrite: false, roughness: 0.5 });
    for (const f of c.flames) {
      (f.mesh as THREE.Mesh).material = flameM;
      f.glow.material.color.set(f.room === c.roomLights.length - 1 ? '#ff4f3a' : '#ff5a24');
      f.glow.scale.setScalar(1.15);
    }
    c.runes.traverse((m) => {
      if ((m as THREE.Mesh).isMesh) ((m as THREE.Mesh).material as THREE.MeshBasicMaterial).color.set('#ff4f3a');
    });
    // Bones and candles go to ash and soot; the wicks keep burning.
    const seen = new Set<THREE.Material>();
    c.decor.traverse((o) => {
      const im = o as THREE.InstancedMesh;
      if (!im.isInstancedMesh) return;
      const mat = im.material as THREE.MeshStandardMaterial;
      if (!mat.isMeshStandardMaterial || seen.has(mat) || (mat.emissive && mat.emissive.getHex() !== 0)) return;
      seen.add(mat);
      const m2 = mat.clone();
      m2.color.lerp(BASALT.char, 0.6);
      im.material = m2;
    });
    scene.background = new THREE.Color('#0a0404');
    if (scene.fog instanceof THREE.Fog) scene.fog.color.set('#120605');
    if (lights.hemi) {
      lights.hemi.color.set('#8a6a66');
      lights.hemi.groundColor.set('#2a0c06');
    }
    if (lights.moon) lights.moon.color.set('#b08a86');
    lights.rooms.forEach((l, i) => {
      l.color.set(i === lights.rooms.length - 1 ? '#ff4a2a' : '#ff8a5a');
    });
  }

  /** Draws the zones of a sim tick: lava cracks (rebuilt when they change) and eruption telegraphs. */
  /** Shows or hides the embers in the air (the deep crypt keeps them to their own floor). */
  ambient(on: boolean): void {
    this.embers.visible = on;
  }

  zones(zones: readonly Zone[]): void {
    let lk = '';
    let ek = '';
    let vk = '';
    for (const z of zones) {
      if (z.kind === 'vent') vk += `${z.id},`;
      if (z.kind === 'lava') lk += `${z.id},`;
      else if (z.kind === 'eruption' && (z.warn ?? 0) > 0) ek += `${z.id},`;
    }
    if (lk !== this.lavaKey) {
      this.lavaKey = lk;
      this.buildLava(zones.filter((z) => z.kind === 'lava'));
    }
    if (vk !== this.ventKey) {
      this.ventKey = vk;
      this.ventList = zones.filter((z) => z.kind === 'vent');
      this.buildVents(this.ventList);
    }
    const live = zones.filter((z) => z.kind === 'eruption' && (z.warn ?? 0) > 0).slice(0, MAX_ERUPT);
    if (ek !== this.eruptKey) {
      this.eruptKey = ek;
      this.buildErupt(live);
    }
    // Progress of each countdown, written per vertex.
    const arr = this.eruptProg.array as Float32Array;
    live.forEach((z, i) => {
      const w = z.warn ?? 0;
      const max = Math.max(this.eruptMax.get(z.id) ?? 0, w, 1);
      this.eruptMax.set(z.id, max);
      const p = clamp01(1 - (w - 1) / max);
      for (let v = 0; v < VERTS; v++) arr[(i * VERTS + v) * 2 + 1] = p;
    });
    this.eruptProg.needsUpdate = true;
    if (this.ventList.length > 0) {
      const sd = this.vents.geometry.getAttribute('aSeed') as THREE.BufferAttribute;
      const va = sd.array as Float32Array;
      this.ventList.forEach((v, i) => {
        const h = this.ventHeat(v, live);
        for (let j = 0; j < VERTS; j++) va[(i * VERTS + j) * 2 + 1] = h;
      });
      sd.needsUpdate = true;
    }
    if (this.eruptMax.size > 64) for (const id of [...this.eruptMax.keys()]) if (!live.some((z) => z.id === id)) this.eruptMax.delete(id);
  }

  /** How far an eruption's countdown has run (0 → 1), for the view's sparks and glow. */
  progress(z: Zone): number {
    const max = Math.max(this.eruptMax.get(z.id) ?? 0, z.warn ?? 0, 1);
    return clamp01(1 - ((z.warn ?? 0) - 1) / max);
  }

  /** How hot a vent burns (0 idle → 1 about to burst): the countdown of an eruption set on it, if any. */
  ventHeat(v: Zone, live?: readonly Zone[]): number {
    const near = (live ?? []).find((z) => Math.abs(z.x - v.x) + Math.abs(z.y - v.y) < 200);
    return near ? this.progress(near) : 0;
  }

  private buildVents(list: Zone[]): void {
    const geo = this.vents.geometry;
    if (list.length === 0) {
      this.vents.visible = false;
      return;
    }
    const pos = new Float32Array(list.length * VERTS * 3);
    const local = new Float32Array(list.length * VERTS * 2);
    const seed = new Float32Array(list.length * VERTS * 2);
    const index: number[] = [];
    list.forEach((z, i) => {
      const d = this.drapeOf(z, 1.18, 0.06);
      pos.set(d.pos, i * VERTS * 3);
      local.set(d.local, i * VERTS * 2);
      // The grate's turn: a fixed angle per vent.
      const s = ((z.id * 0.6180339) % 1) * Math.PI;
      for (let v = 0; v < VERTS; v++) seed[(i * VERTS + v) * 2] = s;
      for (const ix of d.index) index.push(ix + i * VERTS);
    });
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aLocal', new THREE.BufferAttribute(local, 2));
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 2).setUsage(THREE.DynamicDrawUsage));
    geo.setIndex(index);
    this.vents.visible = true;
  }

  private drapeOf(z: Zone, reach: number, lift: number): Draped {
    let d = this.drapes.get(z.id);
    if (!d) {
      d = drape(z.x * K, z.y * K, (z.radius / 1000) * WORLD_PER_HEX, reach, this.ground, lift);
      this.drapes.set(z.id, d);
    }
    return d;
  }

  private buildLava(list: Zone[]): void {
    const geo = this.lava.geometry;
    if (list.length === 0) {
      this.lava.visible = false;
      return;
    }
    const pos = new Float32Array(list.length * VERTS * 3);
    const local = new Float32Array(list.length * VERTS * 2);
    const seed = new Float32Array(list.length * VERTS * 2);
    const index: number[] = [];
    list.forEach((z, i) => {
      const d = this.drapeOf(z, 1.24, 0.07);
      pos.set(d.pos, i * VERTS * 3);
      local.set(d.local, i * VERTS * 2);
      const s = ((z.id * 0.6180339) % 1) * 7;
      for (let v = 0; v < VERTS; v++) seed[(i * VERTS + v) * 2] = s;
      for (const ix of d.index) index.push(ix + i * VERTS);
    });
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aLocal', new THREE.BufferAttribute(local, 2));
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 2));
    geo.setIndex(index);
    this.lava.visible = true;
  }

  private buildErupt(list: Zone[]): void {
    const geo = this.erupt.geometry;
    if (list.length === 0) {
      this.erupt.visible = false;
      return;
    }
    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    const local = geo.getAttribute('aLocal') as THREE.BufferAttribute;
    const parr = pos.array as Float32Array;
    const larr = local.array as Float32Array;
    const sarr = this.eruptProg.array as Float32Array;
    const index: number[] = [];
    list.forEach((z, i) => {
      const d = this.drapeOf(z, 1.6, 0.08);
      parr.set(d.pos, i * VERTS * 3);
      larr.set(d.local, i * VERTS * 2);
      const s = ((z.id * 0.7548776) % 1) * 5;
      for (let v = 0; v < VERTS; v++) sarr[(i * VERTS + v) * 2] = s;
      for (const ix of d.index) index.push(ix + i * VERTS);
    });
    pos.needsUpdate = true;
    local.needsUpdate = true;
    geo.setIndex(index);
    this.erupt.visible = true;
    // Eruptions don't come back: forget the ground under the ones that are gone.
    if (this.drapes.size > 96) for (const id of [...this.drapes.keys()]) if (!list.some((z) => z.id === id) && !this.lavaKey.includes(`${id},`) && !this.ventKey.includes(`${id},`)) this.drapes.delete(id);
  }

  /** Every animation frame: the veins breathe, the lava flows, the embers rise. */
  frame(now: number): void {
    this.uniforms.uTime.value = now % 3600;
  }

  dispose(): void {
    this.embers.geometry.dispose();
    (this.embers.material as THREE.Material).dispose();
    this.lava.geometry.dispose();
    (this.lava.material as THREE.Material).dispose();
    this.erupt.geometry.dispose();
    (this.erupt.material as THREE.Material).dispose();
    this.vents.geometry.dispose();
    (this.vents.material as THREE.Material).dispose();
  }
}

/** Ascuas colours for the view's bursts. */
export const ASCUAS = { ember: BASALT.ember, red: new THREE.Color('#ff4f3a'), hot: new THREE.Color('#ffb060'), magma: new THREE.Color('#ff6a1f') };
